/* ============================================================================
   OVID · Terms & Privacy page styles — Maison tokens
   Shared by _layout.terms.tsx and _layout.privacy.tsx.
   No @use of sibling partials needed — all tokens are :root --ov-* from
   index.scss, which app/root.tsx loads for the whole product bundle.
   ============================================================================ */

/* ── Page wrapper ──────────────────────────────────────────────────────────── */

.terms {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: var(--ov-s9) var(--ov-gutter) var(--ov-s10);
    min-height: 100vh;
    background: var(--ov-dusk);
}

.document {
    max-width: 720px;
    width: 100%;
    color: var(--ov-ivory);
    font-family: var(--ov-grotesque);
    font-size: 15px;
    line-height: var(--ov-lh-body);
}

/* ── Draft banner ──────────────────────────────────────────────────────────── */

.draftBanner {
    margin-bottom: var(--ov-s7);
    padding: 14px 20px;
    border: 1px solid var(--ov-ember);
    border-left: 3px solid var(--ov-ember);
    background: rgba(180, 113, 76, .08);
    color: var(--ov-ash);
    font-size: 13.5px;
    line-height: 1.6;

    strong {
        color: var(--ov-ivory);
        font-weight: 600;
    }
}

/* ── Table of contents ─────────────────────────────────────────────────────── */

.tableOfContents {
    margin-bottom: var(--ov-s8);
    padding: var(--ov-s5) var(--ov-s6);
    background: var(--ov-panel);
    border: 1px solid var(--ov-line);

    h3 {
        font-family: var(--ov-grotesque);
        font-size: 10.5px;
        font-weight: 500;
        letter-spacing: .28em;
        text-transform: uppercase;
        color: var(--ov-dim);
        margin: 0 0 var(--ov-s4);
        padding: 0;
    }

    ol {
        margin: 0;
        padding-left: var(--ov-s5);
        list-style: decimal;
        display: flex;
        flex-direction: column;
        gap: var(--ov-s2);
    }

    a {
        color: var(--ov-ash);
        text-decoration: none;
        font-size: 13.5px;
        transition: color var(--ov-dur-2) var(--ov-ease);

        &:hover {
            color: var(--ov-ivory);
        }
    }
}

/* ── Sections ──────────────────────────────────────────────────────────────── */

.section {
    padding-top: var(--ov-s6);
    margin-bottom: var(--ov-s8);
    border-top: 1px solid var(--ov-line);

    h1 {
        font-family: var(--ov-display);
        font-size: 32px;
        font-weight: 500;
        font-style: italic;
        color: var(--ov-ivory);
        margin: 0 0 var(--ov-s2);
        padding: 0;
        line-height: var(--ov-lh-display);
    }

    h2 {
        font-family: var(--ov-grotesque);
        font-size: 13px;
        font-weight: 600;
        letter-spacing: .18em;
        text-transform: uppercase;
        color: var(--ov-ivory);
        margin: var(--ov-s7) 0 var(--ov-s4);
        padding: 0;
        line-height: 1.4;
    }

    h3 {
        font-family: var(--ov-grotesque);
        font-size: 12px;
        font-weight: 600;
        letter-spacing: .14em;
        text-transform: uppercase;
        color: var(--ov-ash);
        margin: var(--ov-s6) 0 var(--ov-s3);
        padding: 0;
        line-height: 1.4;
    }

    p {
        margin: 0 0 var(--ov-s4);
        padding: 0;
        color: var(--ov-ash);
        font-size: 15px;
        line-height: var(--ov-lh-body);
    }

    a {
        color: var(--ov-ivory);
        text-decoration: underline;
        text-decoration-color: var(--ov-ember);
        text-underline-offset: 2px;
        transition: color var(--ov-dur-2) var(--ov-ease);

        &:hover {
            color: var(--ov-ash);
        }
    }

    ul,
    ol {
        margin: 0 0 var(--ov-s5);
        padding-left: var(--ov-s5);
        color: var(--ov-ash);

        li {
            margin-bottom: var(--ov-s2);
            line-height: var(--ov-lh-body);
        }
    }

    strong {
        color: var(--ov-ivory);
        font-weight: 600;
    }
}

.lastUpdated {
    font-size: 12px !important;
    color: var(--ov-dim) !important;
    letter-spacing: .04em;
    font-style: normal;
    margin-bottom: var(--ov-s6) !important;
}

.emphasis {
    padding: var(--ov-s4) var(--ov-s5);
    background: rgba(180, 113, 76, .07);
    border-left: 2px solid var(--ov-ember);
    color: var(--ov-ivory) !important;
    font-size: 14px !important;
    line-height: 1.7 !important;
    margin-bottom: var(--ov-s4);
}

.zeroPolicyNotice {
    font-family: var(--ov-grotesque);
    font-weight: 600;
    font-size: 12px !important;
    letter-spacing: .12em;
    text-transform: uppercase;
    color: var(--ov-ember) !important;
    padding: 10px var(--ov-s5);
    background: rgba(180, 113, 76, .08);
    border-left: 2px solid var(--ov-ember);
    margin-bottom: var(--ov-s4);
}

.definitionList {
    li {
        margin-bottom: 10px;
    }
}

/* ── Quick reference (AUP) ─────────────────────────────────────────────────── */

.quickReference {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: var(--ov-s5);
    margin: var(--ov-s6) 0;

    @media (max-width: 640px) {
        grid-template-columns: 1fr;
    }
}

.quickReferenceColumn {
    padding: var(--ov-s5);
    border: 1px solid var(--ov-line);
    background: var(--ov-panel);

    h3 {
        margin-top: 0 !important;
        margin-bottom: var(--ov-s4) !important;
        font-size: 10px !important;
        letter-spacing: .22em;
    }

    ul {
        margin: 0;
        padding-left: var(--ov-s4);
        font-size: 13.5px;
    }
}

.allowed {
    color: var(--ov-ivory) !important;
}

.prohibited {
    color: var(--ov-ember) !important;
}

/* ── Enforcement table ─────────────────────────────────────────────────────── */

.enforcementTable {
    display: flex;
    flex-direction: column;
    gap: 1px;
    margin: var(--ov-s5) 0;
    background: var(--ov-line);
    border: 1px solid var(--ov-line);
    overflow: hidden;
}

.enforcementRow {
    display: grid;
    grid-template-columns: 100px 1fr;
    gap: var(--ov-s5);
    padding: var(--ov-s4) var(--ov-s5);
    background: var(--ov-panel);

    &:not(:last-child) {
        border-bottom: 1px solid var(--ov-line);
    }

    p {
        margin: 0 0 var(--ov-s2);
        font-size: 13.5px;

        &:last-child {
            margin-bottom: 0;
        }
    }

    @media (max-width: 640px) {
        grid-template-columns: 1fr;
        gap: var(--ov-s3);
    }
}

.enforcementSeverity {
    display: flex;
    align-items: flex-start;
    padding-top: 2px;
}

.severityCritical {
    font-family: var(--ov-grotesque);
    font-size: 10px;
    font-weight: 600;
    letter-spacing: .14em;
    text-transform: uppercase;
    padding: 3px 8px;
    background: rgba(180, 113, 76, .15);
    color: var(--ov-ember);
}

.severityHigh {
    font-family: var(--ov-grotesque);
    font-size: 10px;
    font-weight: 600;
    letter-spacing: .14em;
    text-transform: uppercase;
    padding: 3px 8px;
    background: rgba(234, 226, 210, .08);
    color: var(--ov-ash);
}

.severityStandard {
    font-family: var(--ov-grotesque);
    font-size: 10px;
    font-weight: 600;
    letter-spacing: .14em;
    text-transform: uppercase;
    padding: 3px 8px;
    background: rgba(234, 226, 210, .05);
    color: var(--ov-dim);
}

/* ── Timeline table ────────────────────────────────────────────────────────── */

.timelineTable {
    margin: var(--ov-s5) 0;
    border: 1px solid var(--ov-line);
    overflow: hidden;
}

.timelineHeader {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr 1fr;
    gap: var(--ov-s4);
    padding: var(--ov-s3) var(--ov-s5);
    background: var(--ov-raise);
    font-family: var(--ov-grotesque);
    font-size: 10px;
    font-weight: 600;
    letter-spacing: .16em;
    text-transform: uppercase;
    color: var(--ov-dim);

    @media (max-width: 640px) {
        display: none;
    }
}

.timelineRow {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr 1fr;
    gap: var(--ov-s4);
    padding: 12px var(--ov-s5);
    font-size: 13px;
    color: var(--ov-ash);
    background: var(--ov-panel);

    &:not(:last-child) {
        border-bottom: 1px solid var(--ov-line);
    }

    @media (max-width: 640px) {
        grid-template-columns: 1fr;
        gap: 2px;
        padding: var(--ov-s4) var(--ov-s5);

        span:first-child {
            font-weight: 600;
            color: var(--ov-ivory);
            margin-bottom: var(--ov-s1);
        }
    }
}

/* ── Back link ─────────────────────────────────────────────────────────────── */

.backLink {
    margin-top: var(--ov-s6);
    padding-top: var(--ov-s6);
    border-top: 1px solid var(--ov-line);
    text-align: center;

    a {
        font-family: var(--ov-grotesque);
        color: var(--ov-dim);
        text-decoration: none;
        font-size: 12px;
        letter-spacing: .14em;
        text-transform: uppercase;
        transition: color var(--ov-dur-2) var(--ov-ease);

        &:hover {
            color: var(--ov-ivory);
        }
    }
}
